// src/components/BBS/Terminal.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';
import { useSessionUnlock } from '../../utils/sessionUnlock';
import { FileSystemState } from '../../terminal/fileSystem';
import { buildCommandMap, handleLoginPassword } from '../../terminal/commands';

const ASCII_BANNER = `
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄
 █                                                     █
 █   ▄███▄ ████▄ █░░░█ ███ ████▄ ███ ▄███▄             █
 █   █░░░█ █░░░█ █████ ░█░ █░░░█ ░█░ █████             █
 █   ▀███▀ █     █░░░█ ███ ████▀ ███ █░░░█             █
 █                                                     █
 █   ▄███▄ █     ▄███▄ ████▄ ▄███▄ █                   █
 █   █░░██ █     █░░░█ █████ █████ █                   █
 █   ▀███▀ █████ ▀███▀ ████▀ █░░░█ █████               █
 █                                                     █
 █▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄█
 ▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
   NODE 7 ░ EST.1987 ░ SYSOP: [REDACTED]
   SIGNAL: ACTIVE ░░░░░░░░░░░░░░░ 97.3%
`.trimEnd();

export default function Terminal() {
  const [lines, setLines] = useState([]);
  const [input, setInput] = useState('');
  const [ready, setReady] = useState(false);
  const [terminalState, setTerminalState] = useState({
    user: null,
    awaitingLogin: null,
    awaitingPassword: false,
    passwordAttempts: 0,
  });

  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const navigate = useNavigate();
  const { unlock } = useSessionUnlock();

  const fsState = useMemo(() => new FileSystemState(), []);

  const addLines = (newLines) => {
    setLines(prev => [...prev, ...newLines]);
  };

  const commandMap = useMemo(() => {
    return buildCommandMap({
      fsState,
      addLines,
      navigate,
      unlock,
      setTerminalState,
      trackEvent,
    });
  // eslint-disable-next-line
  }, [fsState]);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [lines]);

  useEffect(() => {
    if (ready && inputRef.current) {
      inputRef.current.focus();
    }
  }, [ready]);

  // Welcome sequence
  useEffect(() => {
    const welcome = [
      { type: 'blank' },
      { type: 'line', text: '  ═══════════════════════════════════════════════', color: '#333' },
      { type: 'blank' },
      { type: 'line', text: '  Welcome to the Ophidia Global BBS terminal.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  ═══════════════════════════════════════════════', color: '#333' },
      { type: 'blank' },
      { type: 'line', text: '  NAVIGATE THIS TERMINAL:', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '    ► Type a command below and press ENTER', color: '#cccccc' },
      { type: 'line', text: '    ► Or click any option to go there directly', color: '#cccccc' },
      { type: 'blank' },
      { type: 'nav-box' },
      { type: 'blank' },
      { type: 'line', text: '  Login as GUEST to access the filesystem.', color: '#555' },
      { type: 'line', text: '  Type HELP for all commands.', color: '#555' },
      { type: 'blank' },
    ];

    let i = 0;
    const interval = setInterval(() => {
      if (i >= welcome.length) {
        clearInterval(interval);
        setReady(true);
        return;
      }
      setLines(prev => [...prev, welcome[i]]);
      i++;
    }, 60);

    return () => clearInterval(interval);
  }, []);

  function goTo(path) {
    navigate(path);
  }

  // ============================================
  // MAIN COMMAND PROCESSOR
  // ============================================
  function processCommand(cmd) {
    const trimmed = cmd.trim();
    const lower = trimmed.toLowerCase();

    if (terminalState.awaitingLogin) {
      handleLoginPassword(
        trimmed,
        fsState,
        terminalState.awaitingLogin,
        addLines,
        setTerminalState,
        unlock
      );
      return;
    }

    if (terminalState.awaitingPassword) {
      handleLegacyRootPassword(trimmed);
      return;
    }

    const prompt = fsState.currentUser
      ? `${fsState.currentUser}@node7:${fsState.currentPath}$ ${cmd}`
      : `> ${cmd}`;
    setLines(prev => [...prev, { type: 'input', text: prompt }]);

    const parts = trimmed.split(/\s+/);
    const command = parts[0].toLowerCase();
    const args = parts.slice(1);

    if (commandMap[lower]) {
      const result = commandMap[lower](args);
      if (result && result.action === 'clear') {
        setLines([]);
      }
      return;
    }

    if (commandMap[command]) {
      const result = commandMap[command](args);
      if (result && result.action === 'clear') {
        setLines([]);
      }
      return;
    }

    // ── CHANGELOG ──
    if (command === 'changelog' || command === 'changes' || command === 'updates') {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  ═══════════════════════════════════════════', color: '#333' },
        { type: 'line', text: '  ▓ OPHIDIA GLOBAL BBS — CHANGELOG ▓', color: '#ffb000' },
        { type: 'line', text: '  ═══════════════════════════════════════════', color: '#333' },
        { type: 'blank' },
        { type: 'line', text: '  [2025-03-23] ░░░░░░░░░░░░░░░░░░░░░░░░░░░░', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  ► OPU-WEB: THREAT ACTIVE ADDED', color: '#00ff88' },
        { type: 'line', text: '    OPU-WEB access termina.', color: '#c8a96e' },
        { type: 'blank' },
        { type: 'line', text: '  [2025-03-18] ░░░░░░░░░░░░░░░░░░░░░░░░░░░░', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  ► THE SHED SKIN — TRUE ENDING ADDED', color: '#00ff88' },
        { type: 'line', text: '    Quest 8: "THE SIGNAL IN THE STATIC"', color: '#c8a96e' },
        { type: 'line', text: '    Dan can finally say her name.', color: '#c8a96e' },
        { type: 'line', text: '    The signal reaches the outside.', color: '#c8a96e' },
        { type: 'line', text: '    Complete all 8 quests for the true ending.', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  [2025-03-04] ░░░░░░░░░░░░░░░░░░░░░░░░░░░░', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  ► THE SHED SKIN — RELEASED', color: '#ff0040' },
        { type: 'line', text: '    A Legend of the Red Dragon tribute.', color: '#c8a96e' },
        { type: 'line', text: '    Cyberpunk undercity RPG. 4 classes.', color: '#c8a96e' },
        { type: 'line', text: '    7 quest chain. 2 bosses. AI bartender.', color: '#c8a96e' },
        { type: 'line', text: '    Dan pours drinks and remembers fragments.', color: '#c8a96e' },
        { type: 'line', text: '    Enter OPU-1103 in the terminal to unlock.', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  [2025-02-25] ░░░░░░░░░░░░░░░░░░░░░░░░░░░░', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  ► OPU-1 — INITIALIZED', color: '#8b00ff' },
        { type: 'line', text: '    The first interactive experience goes live.', color: '#c8a96e' },
        { type: 'line', text: '    An organic processing unit wakes up.', color: '#c8a96e' },
        { type: 'line', text: '    It is waiting for you underneath.', color: '#c8a96e' },
        { type: 'line', text: '    Ophidia Global BBS launched.', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  ═══════════════════════════════════════════', color: '#333' },
        { type: 'line', text: '  End of changelog. Type HELP for commands.', color: '#555' },
        { type: 'line══════════════════════════════════════════', color: '#333' },
        { type: 'blank' },
      ]);
      trackEvent('command', 'terminal', 'changelog');
      return;
    }

    // ── Secret code: unlocks The Shed Skin (Dan LORD game) ──
    if (lower === 'opu-1103' || lower === 'opu 1103' || lower === 'opu1103') {
      unlock('dan_lord');
      sessionStorage.setItem('unlock_dan_lord', 'true');
      sessionStorage.setItem('dan_lord_unlocked', 'true');
      window.dispatchEvent(new CustomEvent('arcade-unlock', { detail: { key: 'dan_lord' } }));

      const sequence = [
        [0,    '', null],
        [200,  '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#ff0040'],
        [400,  '', null],
        [600,  '  DESIGNATION RECOGNIZED.', '#ff0040'],
        [1000, '  OPU-1103 — KOWALSKI, D.', '#ff0040'],
        [1400, '', null],
        [1800, '  Tracing distress signal...', '#cc8888'],
        [2200, '  ████████████████████████████ 100%', '#cc8888'],
        [2600, '', null],
        [3000, '  Signal origin: THE SHED SKIN', '#ff0040'],
        [3400, '  Undercity sublevel 7, sector 12', '#555'],
        [3800, '', null],
        [4200, '  He is still in there.', '#c8a96e'],
        [4600, '  Behind the bar. Behind the code.', '#c8a96e'],
        [5000, '  Pouring drinks with hands that are not his.', '#c8a96e'],
        [5400, '', null],
        [5800, '  ► THE SHED SKIN has been unlocked in the GAMES arcade.', '#00ff88'],
        [6200, '', null],
        [6600, '  "Help me. I can hear myself screaming."', '#555'],
        [7000, '  — OPU-1103', '#555'],
        [7400, '', null],
        [7800, '  🐍', '#00ff41'],
        [8200, '', null],
        [8600, '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#ff0040'],
        [9000, '', null],
      ];

      sequence.forEach(([delay, text, color]) => {
        setTimeout(() => {
          addLines([color ? { type: 'line', text, color } : { type: 'blank' }]);
        }, delay);
      });

      trackEvent('easter_egg', 'terminal', 'opu1103_dan_unlock');
      return;
    }

    if (lower.startsWith('chrysalis')) {
      const keyPart = lower.replace('chrysalis', '').trim();
      if (commandMap['chrysalis']) {
        commandMap['chrysalis']([keyPart]);
        return;
      }
    }

    if (lower.startsWith('opuweb')) {
      const keyPart = lower.replace('opuweb', '').trim();
      if (commandMap['opuweb']) {
        commandMap['opuweb']([keyPart]);
        return;
      }
    }

    if (lower.startsWith('parallax')) {
      const keyPart = lower.replace('parallax', '').trim();
      if (commandMap['parallax']) {
        commandMap['parallax']([keyPart]);
        return;
      }
    }

    if (['root', 'su', 'sudo'].includes(lower)) {
      setTerminalState(prev => ({
        ...prev,
        awaitingPassword: true,
        passwordAttempts: 0,
      }));

      addLines([
        { type: 'blank' },
        { type: 'line', text: '  ═══════════════════════════════════════════', color: '#00ff41' },
        { type: 'blank' },
        { type: 'line', text: '  🐍 OPHIDIA GLOBAL — NETWORK ADMINISTRATION 🐍', color: '#00ff41' },
        { type: 'blank' },
        { type: 'line', text: '  ═══════════════════════════════════════════', color: '#00ff41' },
        { type: 'blank' },
        { type: 'line', text: '  WARNING: Unauthorized access to this system', color: '#ff0040' },
        { type: 'line', text: '  is a violation of Ophidia Global Directive 7.', color: '#ff0040' },
        { type: 'blank' },
        { type: 'line', text: '  All connection metadata is being recorded.', color: '#555' },
        { type: 'line', text: '  Biometric scan initiated via display reflection.', color: '#555' },
        { type: 'blank' },
        { type: 'line', text: '  Enter root password:', color: '#ffb000' },
      ]);
      trackEvent('easter_egg', 'terminal', 'root_attempt');
      return;
    }

    const unlockedKey = localStorage.getItem(`hashmap_${lower}`);
    if (unlockedKey) {
      unlock(unlockedKey);
      if (unlockedKey === 'opu1') {
        sessionStorage.setItem('opu1_hash_reentry', 'true');
      }
      window.dispatchEvent(new CustomEvent('arcade-unlock', { detail: { key: unlockedKey } }));

      const keyNames = { opu1: 'O P U - 1', underbelly: 'THE BIRTHING' };
      const gameName = keyNames[unlockedKey] || unlockedKey.toUpperCase();

      const hashSequence = [
        [0,    '', null],
        [100,  '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#8b00ff'],
        [300,  '', null],
        [500,  '  HASH RECOGNIZED.', '#8b00ff'],
        [800,  '  Validating neural signature...', '#553388'],
        [1200, '  ████████████████████████████ 100%', '#553388'],
        [1600, '', null],
        [1900, '  ▓ ACCESS GRANTED ▓', '#cc88ff'],
        [2200, '', null],
        [2500, `  ${gameName} has been re-activated.`, '#aa77cc'],
        [2800, '  Navigate to GAMES to access it.', '#aa77cc'],
        [3100, '', null],
        [3300, '  The old brain remembers you.', '#8b00ff'],
        [3500, '  It has been waiting.', '#8b00ff'],
        [3700, '', null],
        [3900, '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#8b00ff'],
        [4100, '', null],
      ];

      hashSequence.forEach(([delay, text, color]) => {
        setTimeout(() => {
          addLines([color ? { type: 'line', text, color } : { type: 'blank' }]);
        }, delay);
      });

      trackEvent('hash_unlock', 'terminal', unlockedKey);
      return;
    }

    // Unknown command
    const unknownResponses = [
      `Command not recognized: "${cmd}". Type HELP for available commands.`,
      `Unknown input: "${cmd}". The signal does not understand. Try HELP.`,
      `"${cmd}" — unrecognized. Try ARCHIVES, GAMES, LOGIN GUEST, or HELP.`,
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${unknownResponses[Math.floor(Math.random() * unknownResponses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  }

  // ============================================
  // LEGACY ROOT PASSWORD HANDLER
  // ============================================
  async function handleLegacyRootPassword(cmd) {
    addLines([{ type: 'input', text: `> ${'•'.repeat(cmd.length)}` }]);

    const trimmed = cmd.trim();

    const { verifyPassword } = await import('../../terminal/auth');
    const isCorrect = await verifyPassword('root', trimmed);

    if (isCorrect) {
      fsState.loginAs('root');

      setTerminalState(prev => ({
        ...prev,
        awaitingPassword: false,
        passwordAttempts: 0,
        user: 'root',
      }));

      const accessSequence = [
        [0, '', null],
        [200, '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#00ff41'],
        [400, '', null],
        [600, '  IDENTITY VERIFIED.', '#00ff41'],
        [900, '  Welcome back, Administrator.', '#00ff41'],
        [1200, '', null],
        [1500, '  ╔═══════════════════════════════════════════╗', '#00ff41'],
        [1600, '  ║  OPHIDIA GLOBAL — NETWORK ADMINISTRATION  ║', '#00ff41'],
        [1700, '  ║  CLEARANCE: LEVEL ████                    ║', '#00ff41'],
        [1800, '  ║  STATUS: COIL PROTOCOL ACTIVE             ║', '#00ff41'],
        [1900, '  ╚═══════════════════════════════════════════╝', '#00ff41'],
        [2200, '', null],
        [2500, '  Home directory: /home/admin', '#00ff41'],
        [2800, '  Type LS to see classified files.', '#00ff41'],
        [3100, '', null],
        [3300, '  The old blood recognizes you.', '#3a8a3a'],
        [3500, '', null],
        [3700, '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', '#00ff41'],
        [3900, '', null],
      ];

      accessSequence.forEach(([delay, text, color]) => {
        setTimeout(() => {
          addLines([color ? { type: 'line', text, color } : { type: 'blank' }]);
        }, delay);
      });

      setTimeout(() => { unlock('underbelly'); }, 2000);
      trackEvent('easter_egg', 'terminal', 'root_access_granted');
      return;
    }

    const newAttempts = terminalState.passwordAttempts + 1;
    setTerminalState(prev => ({ ...prev, passwordAttempts: newAttempts }));

    const fakeIP = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
    const terminalID = Math.random().toString(36).substring(2, 10).toUpperCase();

    if (newAttempts >= 3) {
      setTerminalState(prev => ({
        ...prev,
        awaitingPassword: false,
        passwordAttempts: 0,
      }));

      const lockoutSequence = [
        [0, '', null],
        [200, '  ▓▓▓ AUTHENTICATION FAILURE ▓▓▓', '#ff0040'],
        [400, '', null],
        [600, '  ████████████████████████████████████████', '#ff0040'],
        [800, '  ██                                    ██', '#ff0040'],
        [900, '  ██   MULTIPLE FAILED LOGIN ATTEMPTS   ██', '#ff0040'],
        [1000, '  ██                                    ██', '#ff0040'],
        [1100, '  ██   TERMINAL FLAGGED FOR REVIEW      ██', '#ff0040'],
        [1200, '  ██                                    ██', '#ff0040'],
        [1300, '  ████████████████████████████████████████', '#ff0040'],
        [1600, '', null],
        [1900, `  IP Address:    ${fakeIP}`, '#ff0040'],
        [2100, `  Terminal ID:   ${terminalID}`, '#ff0040'],
        [2300, `  Timestamp:     ${new Date().toISOString()}`, '#ff0040'],
        [2500, `  Attempts:      ${newAttempts}/3 — THRESHOLD EXCEEDED`, '#ff0040'],
        [2700, '', null],
        [3000, '  Your biometric signature has been forwarded', '#555'],
        // CHANGED: "Ophidia Global Security Division" was already correct — no change needed
        [3200, '  to Ophidia Global IT.', '#555'],
        [3500, '', null],
        [3700, '  Use LOGIN GUEST to explore the filesystem.', '#333'],
        [4100, '', null],
        [4300, '  🐍', '#00ff41'],
        [4500, '', null],
      ];

      lockoutSequence.forEach(([delay, text, color]) => {
        setTimeout(() => {
          addLines([color ? { type: 'line', text, color } : { type: 'blank' }]);
        }, delay);
      });

      trackEvent('easter_egg', 'terminal', 'root_lockout');
    } else {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  ▓ AUTHENTICATION FAILED ▓', color: '#ff0040' },
        { type: 'blank' },
        { type: 'line', text: '  Incorrect password. Your IP has been logged.', color: '#ff0040' },
        { type: 'blank' },
        { type: 'line', text: `  IP Address:    ${fakeIP}`, color: '#555' },
        { type: 'line', text: `  Terminal ID:   ${terminalID}`, color: '#555' },
        { type: 'line', text: `  Timestamp:     ${new Date().toISOString()}`, color: '#555' },
        { type: 'line', text: `  Attempt:       ${newAttempts}/3`, color: '#ff0040' },
        { type: 'blank' },
        { type: 'line', text: '  Enter root password:', color: '#ffb000' },
      ]);
    }
  }

  // ============================================
  // FORM SUBMIT
  // ============================================
  function handleSubmit(e) {
    e.preventDefault();
    if (!input.trim() || !ready) return;
    processCommand(input.trim());
    setInput('');
  }

  // ============================================
  // LINE RENDERER
  // ============================================
  function renderLine(line, index) {
    if (!line || !line.type) return null;

    if (line.type === 'blank') {
      return <div key={index} style={{ height: '1.2em' }} />;
    }

    if (line.type === 'nav-box') {
      return (
        <div key={index} style={{
          border: '1px solid #333',
          padding: '15px 20px',
          margin: '5px 15px',
        }}>
          <NavOption label="[1] ARCHIVES" desc="Archived Transcripts" onClick={() => goTo('/archives')} />
          <NavOption label="[2] LINKS" desc="External Media" onClick={() => goTo('/links')} />
          <NavOption label="[3] GAMES" desc="Interactive Experiences" onClick={() => goTo('/games')} />
          <NavOption label="[4] LIVE FEED" desc="Visual Media" onClick={() => goTo('/feed')} />
          <NavOption label="[5] ABOUT" desc="About the Ophidia Global BBS terminal" onClick={() => processCommand('about')} />
          <NavOption label="[6] SYNC" desc="Sync to the Terminal" onClick={() => goTo('/subscribe')} />
        </div>
      );
    }

    if (line.type === 'input') {
      return (
        <div key={index} style={{ color: '#00ff41', fontWeight: 'bold' }}>
          {line.text}
        </div>
      );
    }

    if (line.type === 'command-help') {
      return (
        <div key={index} style={{ fontFamily: "'VT323', 'Courier New', monospace" }}>
          <span style={{ color: line.color || '#00ff41', fontWeight: 'bold' }}>{line.text}</span>
          <span style={{ color: '#555' }}>{line.desc}</span>
        </div>
      );
    }

    return (
      <div key={index} style={{ color: line.color || '#cccccc' }}>
        {line.text}
      </div>
    );
  }

  // ============================================
  // Determine input mode
  // ============================================
  const isPasswordMode = terminalState.awaitingPassword || terminalState.awaitingLogin;
  const promptSymbol = isPasswordMode ? '🔒' : (
    fsState.currentUser ? `${fsState.currentUser}@node7:${fsState.currentPath}$` : '>'
  );

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>

      {/* Static ASCII Banner */}
      <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
        <pre style={{
          color: '#ff0040',
          fontSize: 'clamp(0.45rem, 2.5vw, 1rem)',
          lineHeight: '1.3',
          fontFamily: "'Courier New', Courier, monospace",
          textShadow: '0 0 15px rgba(255, 0, 64, 0.5), 0 0 30px rgba(255, 0, 64, 0.2)',
          margin: '0 auto',
          display: 'inline-block',
          textAlign: 'left',
          whiteSpace: 'pre',
          letterSpacing: '0.05em',
        }}>
{ASCII_BANNER}
        </pre>
        <div style={{
          color: '#ff0040',
          fontSize: '0.85rem',
          marginTop: '12px',
          fontFamily: "'VT323', monospace",
          letterSpacing: '3px',
        }}>
          ▓ GOD IS WIRE. GOD IS FEAR. ▓
        </div>
      </div>

      {/* Terminal output */}
      <div
        ref={outputRef}
        onClick={() => inputRef.current?.focus()}
        style={{
          minHeight: '300px',
          maxHeight: '55vh',
          overflowY: 'auto',
          padding: '20px',
          fontFamily: "'VT323', 'Courier New', monospace",
          fontSize: '1.1rem',
          lineHeight: '1.5',
          cursor: 'text',
        }}
      >
        {lines.map((line, i) => renderLine(line, i))}
      </div>

      {/* Input */}
      {ready && (
        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 20px',
          borderTop: '1px solid #333',
          gap: '10px',
        }}>
          <span style={{
            color: isPasswordMode ? '#ff0040' : '#00ff41',
            fontSize: isPasswordMode ? '1.2rem' : '0.9rem',
            fontFamily: "'VT323', monospace",
            whiteSpace: 'nowrap',
            minWidth: isPasswordMode ? 'auto' : undefined,
          }}>
            {promptSymbol}
          </span>
          <input
            ref={inputRef}
            type={isPasswordMode ? 'password' : 'text'}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={
              isPasswordMode
                ? 'Enter password...'
                : 'Type a command... (try ARCHIVES, GAMES, LOGIN GUEST, or HELP)'
            }
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: isPasswordMode ? '#ff0040' : '#00ff41',
              fontFamily: "'VT323', 'Courier New', monospace",
              fontSize: '1.2rem',
              caretColor: isPasswordMode ? '#ff0040' : '#00ff41',
            }}
          />
          <button
            type="submit"
            className="bbs-button"
            style={{ padding: '5px 15px', fontSize: '0.9rem' }}
          >
            ENTER
          </button>
        </form>
      )}
    </div>
  );
}

function NavOption({ label, desc, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 10px',
        margin: '2px 0',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #1a1a1a',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'rgba(0, 255, 65, 0.05)';
        e.currentTarget.style.borderLeft = '3px solid #00ff41';
        e.currentTarget.style.paddingLeft = '12px';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderLeft = '3px solid transparent';
        e.currentTarget.style.paddingLeft = '10px';
      }}
    >
      <span style={{
        color: '#00ff41',
        fontFamily: "'VT323', 'Courier New', monospace",
        fontSize: '1.15rem',
        fontWeight: 'bold',
      }}>
        {label}
      </span>
      <span style={{
        color: '#555',
        fontFamily: "'VT323', 'Courier New', monospace",
        fontSize: '1rem',
      }}>
        {desc}
      </span>
    </div>
  );
}